import { useState, useMemo } from "react";
import { Lock, Download, Users, LogOut, Eye, EyeOff, Trash2, Search, Filter, X, Check, AlertTriangle, Mail, Zap } from "lucide-react";

const TRACKING_FIELDS = [
  { key: "telegram_added", label: "TG Added" },
  { key: "discord_added", label: "Discord" },
  { key: "courses_distributed", label: "Courses" },
  { key: "onboarded", label: "Onboarded" },
  { key: "broker_account_created", label: "Broker Acct" },
];

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [storedPassword, setStoredPassword] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteModal, setDeleteModal] = useState(null);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [reminderToast, setReminderToast] = useState(null); // { type: 'success'|'error', message }
  const [reminderBusyId, setReminderBusyId] = useState(null);
  const [bulkRunBusy, setBulkRunBusy] = useState(false);
  const [bulkOptInModal, setBulkOptInModal] = useState(false);
  const [bulkOptInBusy, setBulkOptInBusy] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (response.ok) {
        setIsAuthenticated(true);
        setStoredPassword(password);
        fetchSubscribers(password);
      } else {
        setError("Invalid password");
      }
    } catch (err) {
      setError("Connection error");
    }
    setLoading(false);
  };

  const fetchSubscribers = async (pwd) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/subscribers?password=${encodeURIComponent(pwd)}`);
      if (response.ok) {
        const data = await response.json();
        setSubscribers(data.subscribers || []);
      }
    } catch (err) {
      setError("Failed to load subscribers");
    }
    setLoading(false);
  };

  const handleExportCSV = () => {
    window.open(`${API}/api/admin/export-csv?password=${encodeURIComponent(storedPassword)}`, "_blank");
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword("");
    setStoredPassword("");
    setSubscribers([]);
    setSelectedIds(new Set());
  };

  const handleToggleTracking = async (subscriberId, field, currentValue) => {
    const newValue = !currentValue;
    setSubscribers((prev) =>
      prev.map((s) => (s.id === subscriberId ? { ...s, [field]: newValue } : s))
    );
    try {
      const res = await fetch(`${API}/api/admin/subscribers/${subscriberId}/tracking`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: storedPassword, field, value: newValue }),
      });
      if (!res.ok) {
        setSubscribers((prev) =>
          prev.map((s) => (s.id === subscriberId ? { ...s, [field]: currentValue } : s))
        );
      }
    } catch {
      setSubscribers((prev) =>
        prev.map((s) => (s.id === subscriberId ? { ...s, [field]: currentValue } : s))
      );
    }
  };

  const handleDeleteSingle = async () => {
    if (!deleteModal) return;
    try {
      const res = await fetch(
        `${API}/api/admin/subscribers/${deleteModal.id}?password=${encodeURIComponent(storedPassword)}`,
        { method: "DELETE" }
      );
      if (res.ok) {
        setSubscribers((prev) => prev.filter((s) => s.id !== deleteModal.id));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(deleteModal.id);
          return next;
        });
      }
    } catch {}
    setDeleteModal(null);
  };

  const handleBulkDelete = async () => {
    try {
      const res = await fetch(`${API}/api/admin/subscribers/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: storedPassword, ids: [...selectedIds] }),
      });
      if (res.ok) {
        setSubscribers((prev) => prev.filter((s) => !selectedIds.has(s.id)));
        setSelectedIds(new Set());
      }
    } catch {}
    setBulkDeleteModal(false);
  };

  const showToast = (type, message) => {
    setReminderToast({ type, message });
    setTimeout(() => setReminderToast(null), 4500);
  };

  const handleSendReminder = async (sub) => {
    setReminderBusyId(sub.id);
    try {
      const res = await fetch(`${API}/api/admin/subscribers/${sub.id}/send-reminder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: storedPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSubscribers((prev) =>
          prev.map((s) =>
            s.id === sub.id
              ? { ...s, reminder_count: data.stage, last_reminder_at: new Date().toISOString() }
              : s
          )
        );
        showToast("success", `Reminder #${data.stage} sent to ${sub.email}`);
      } else {
        showToast("error", data.detail || "Failed to send reminder");
      }
    } catch {
      showToast("error", "Network error");
    }
    setReminderBusyId(null);
  };

  const handleRunRemindersNow = async () => {
    setBulkRunBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/reminders/run-now`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: storedPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showToast("success", `Auto-reminder pass complete — ${data.sent} email(s) sent`);
        await fetchSubscribers(storedPassword);
      } else {
        showToast("error", data.detail || "Failed to run reminders");
      }
    } catch {
      showToast("error", "Network error");
    }
    setBulkRunBusy(false);
  };

  const handleBulkOptIn = async () => {
    setBulkOptInBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/reminders/bulk-enable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: storedPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showToast(
          "success",
          `Auto-reminders enabled for ${data.enabled_count} pending lead(s). The 24h/72h/7d sequence will start on next hourly check.`
        );
        await fetchSubscribers(storedPassword);
      } else {
        showToast("error", data.detail || "Failed to enable auto-reminders");
      }
    } catch {
      showToast("error", "Network error");
    }
    setBulkOptInBusy(false);
    setBulkOptInModal(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const filteredSubscribers = useMemo(() => {
    let result = subscribers;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          (s.name || "").toLowerCase().includes(q) ||
          (s.email || "").toLowerCase().includes(q) ||
          (s.telegram || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") {
      result = result.filter((s) => {
        if (statusFilter === "complete") {
          return TRACKING_FIELDS.every((f) => s[f.key]);
        }
        if (statusFilter === "incomplete") {
          return !TRACKING_FIELDS.every((f) => s[f.key]);
        }
        return !s[statusFilter];
      });
    }
    return result;
  }, [subscribers, searchQuery, statusFilter]);

  const selectAllFiltered = () => {
    const allIds = filteredSubscribers.map((s) => s.id);
    const allSelected = allIds.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const allFilteredSelected =
    filteredSubscribers.length > 0 && filteredSubscribers.every((s) => selectedIds.has(s.id));

  const completedCount = subscribers.filter((s) => TRACKING_FIELDS.every((f) => s[f.key])).length;
  const pendingOnboardingCount = subscribers.filter((s) => !s.onboarded).length;

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="admin-login-wrapper" data-testid="admin-login-wrapper">
        <div className="admin-login-container">
          <div className="admin-login-header">
            <div className="admin-lock-icon"><Lock size={32} /></div>
            <h1>Admin Access</h1>
            <p>Enter password to view subscribers</p>
          </div>
          <form onSubmit={handleLogin} className="admin-login-form">
            <div className="admin-input-group">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="admin-input"
                required
                data-testid="admin-password-input"
              />
              <button type="button" className="admin-toggle-password" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {error && <p className="admin-error">{error}</p>}
            <button type="submit" className="admin-btn-primary" disabled={loading} data-testid="admin-login-btn">
              {loading ? "Verifying..." : "Login"}
            </button>
          </form>
          <a href="/" className="admin-back-link">Back to Home</a>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      {/* Toast for reminder actions */}
      {reminderToast && (
        <div
          className={`admin-toast admin-toast-${reminderToast.type}`}
          data-testid="admin-toast"
        >
          {reminderToast.message}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="admin-modal-overlay" onClick={() => setDeleteModal(null)} data-testid="delete-confirm-modal">
          <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
            <AlertTriangle size={40} className="admin-modal-icon" />
            <h3>Delete Subscriber</h3>
            <p>Are you sure you want to delete <strong>{deleteModal.name || deleteModal.email}</strong>? This cannot be undone.</p>
            <div className="admin-modal-actions">
              <button className="admin-btn-cancel" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="admin-btn-danger" onClick={handleDeleteSingle} data-testid="confirm-delete-btn">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteModal && (
        <div className="admin-modal-overlay" onClick={() => setBulkDeleteModal(false)} data-testid="bulk-delete-confirm-modal">
          <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
            <AlertTriangle size={40} className="admin-modal-icon" />
            <h3>Delete {selectedIds.size} Subscriber{selectedIds.size > 1 ? "s" : ""}</h3>
            <p>Are you sure you want to delete <strong>{selectedIds.size}</strong> selected subscriber{selectedIds.size > 1 ? "s" : ""}? This cannot be undone.</p>
            <div className="admin-modal-actions">
              <button className="admin-btn-cancel" onClick={() => setBulkDeleteModal(false)}>Cancel</button>
              <button className="admin-btn-danger" onClick={handleBulkDelete} data-testid="confirm-bulk-delete-btn">Delete All</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Opt-In Confirmation Modal */}
      {bulkOptInModal && (
        <div className="admin-modal-overlay" onClick={() => setBulkOptInModal(false)} data-testid="bulk-optin-modal">
          <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
            <Mail size={40} className="admin-modal-icon" style={{ color: "var(--accent-gold)" }} />
            <h3>Enable Auto-Reminders</h3>
            <p>
              This will turn on the <strong>24h / 72h / 7d</strong> reminder sequence for all{" "}
              <strong>{pendingOnboardingCount}</strong> lead{pendingOnboardingCount === 1 ? "" : "s"} who haven't completed Step 2 yet.
            </p>
            <p style={{ fontSize: 13, opacity: 0.75, marginTop: 8 }}>
              Reminders only fire when the time threshold is met (e.g. signed up &gt;24h ago → reminder 1). Already-onboarded leads are excluded.
              Each lead receives a maximum of 3 reminder emails.
            </p>
            <div className="admin-modal-actions">
              <button className="admin-btn-cancel" onClick={() => setBulkOptInModal(false)}>Cancel</button>
              <button
                className="admin-btn-confirm-gold"
                onClick={handleBulkOptIn}
                disabled={bulkOptInBusy}
                data-testid="confirm-bulk-optin-btn"
              >
                {bulkOptInBusy ? "Enabling..." : `Enable for ${pendingOnboardingCount} lead${pendingOnboardingCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="admin-header">
        <div className="admin-header-left">
          <Users size={24} />
          <h1>Subscriber Management</h1>
        </div>
        <div className="admin-header-right">
          {selectedIds.size > 0 && (
            <button onClick={() => setBulkDeleteModal(true)} className="admin-btn-bulk-delete" data-testid="bulk-delete-btn">
              <Trash2 size={16} />
              Delete ({selectedIds.size})
            </button>
          )}
          <button
            onClick={() => setBulkOptInModal(true)}
            className="admin-btn-export admin-btn-bulk-optin"
            disabled={bulkOptInBusy || pendingOnboardingCount === 0}
            title="Enable auto-reminder sequence for all pending Step 2 leads (incl. legacy)"
            data-testid="bulk-optin-btn"
          >
            <Mail size={18} />
            Enable Auto-Reminders
          </button>
          <button
            onClick={handleRunRemindersNow}
            className="admin-btn-export"
            disabled={bulkRunBusy}
            title="Run the auto-reminder check now (24h / 72h / 7d)"
            data-testid="run-reminders-btn"
          >
            <Zap size={18} />
            {bulkRunBusy ? "Running..." : "Run Reminders"}
          </button>
          <button onClick={handleExportCSV} className="admin-btn-export" data-testid="export-csv-btn">
            <Download size={18} />
            Export CSV
          </button>
          <button onClick={handleLogout} className="admin-btn-logout">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </header>

      <main className="admin-main">
        {/* Stats */}
        <div className="admin-stats">
          <div className="admin-stat-card">
            <span className="admin-stat-number" data-testid="stat-total">{subscribers.length}</span>
            <span className="admin-stat-label">Total Subscribers</span>
          </div>
          <div className="admin-stat-card">
            <span className="admin-stat-number" data-testid="stat-active">{subscribers.filter((s) => s.status === "active").length}</span>
            <span className="admin-stat-label">Active</span>
          </div>
          <div className="admin-stat-card">
            <span className="admin-stat-number admin-stat-gold" data-testid="stat-complete">{completedCount}</span>
            <span className="admin-stat-label">Fully Onboarded</span>
          </div>
          <div className="admin-stat-card">
            <span
              className="admin-stat-number"
              style={{ color: pendingOnboardingCount > 0 ? "#ef4444" : "#9ca3af" }}
              data-testid="stat-pending-onboarding"
            >
              {pendingOnboardingCount}
            </span>
            <span className="admin-stat-label">Pending Step 2</span>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="admin-toolbar" data-testid="admin-toolbar">
          <div className="admin-search-box">
            <Search size={18} className="admin-search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, or telegram..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-search-input"
              data-testid="search-input"
            />
            {searchQuery && (
              <button className="admin-search-clear" onClick={() => setSearchQuery("")}><X size={16} /></button>
            )}
          </div>
          <div className="admin-filter-box">
            <Filter size={16} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="admin-filter-select"
              data-testid="status-filter"
            >
              <option value="all">All Subscribers</option>
              <option value="complete">Fully Onboarded</option>
              <option value="incomplete">Incomplete</option>
              <option value="telegram_added">Missing: Telegram</option>
              <option value="discord_added">Missing: Discord</option>
              <option value="courses_distributed">Missing: Courses</option>
              <option value="onboarded">Missing: Onboarded</option>
              <option value="broker_account_created">Missing: Broker Acct</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="admin-loading">Loading subscribers...</div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table" data-testid="admin-table">
              <thead>
                <tr>
                  <th className="admin-th-check">
                    <label className="admin-checkbox-wrap">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={selectAllFiltered}
                        data-testid="select-all-checkbox"
                      />
                      <span className="admin-checkbox-custom"></span>
                    </label>
                  </th>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>WhatsApp</th>
                  <th>Telegram</th>
                  <th>Referred By</th>
                  <th>How Found</th>
                  {TRACKING_FIELDS.map((f) => (
                    <th key={f.key} className="admin-th-tracking">{f.label}</th>
                  ))}
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscribers.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="admin-empty">
                      {searchQuery || statusFilter !== "all" ? "No subscribers match your filters" : "No subscribers yet"}
                    </td>
                  </tr>
                ) : (
                  filteredSubscribers.map((sub, index) => (
                    <tr key={sub.id || index} className={selectedIds.has(sub.id) ? "admin-row-selected" : ""}>
                      <td>
                        <label className="admin-checkbox-wrap">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(sub.id)}
                            onChange={() => toggleSelect(sub.id)}
                            data-testid={`select-${sub.id}`}
                          />
                          <span className="admin-checkbox-custom"></span>
                        </label>
                      </td>
                      <td className="admin-td-num">{index + 1}</td>
                      <td>{sub.name || "-"}</td>
                      <td className="admin-td-email">{sub.email}</td>
                      <td>{sub.whatsapp || "-"}</td>
                      <td>{sub.telegram || "-"}</td>
                      <td>{sub.referred_by || "-"}</td>
                      <td>{sub.how_found || "-"}</td>
                      {TRACKING_FIELDS.map((f) => (
                        <td key={f.key} className="admin-td-tracking">
                          <button
                            className={`admin-track-btn ${sub[f.key] ? "admin-track-done" : "admin-track-pending"}`}
                            onClick={() => handleToggleTracking(sub.id, f.key, sub[f.key])}
                            title={sub[f.key] ? `${f.label}: Done` : `${f.label}: Pending`}
                            data-testid={`track-${f.key}-${sub.id}`}
                          >
                            {sub[f.key] ? <Check size={16} /> : <span className="admin-track-dash">-</span>}
                          </button>
                        </td>
                      ))}
                      <td className="admin-td-date">{formatDate(sub.subscribed_at)}</td>
                      <td>
                        <div className="admin-row-actions">
                          {!sub.onboarded && (
                            <button
                              className="admin-btn-reminder"
                              onClick={() => handleSendReminder(sub)}
                              disabled={reminderBusyId === sub.id || (sub.reminder_count || 0) >= 3}
                              title={
                                (sub.reminder_count || 0) >= 3
                                  ? "All 3 reminders already sent"
                                  : `Send reminder #${(sub.reminder_count || 0) + 1} of 3 now`
                              }
                              data-testid={`send-reminder-${sub.id}`}
                            >
                              <Mail size={14} />
                              <span className="admin-reminder-count">
                                {sub.reminder_count || 0}/3
                              </span>
                            </button>
                          )}
                          <button
                            className="admin-btn-delete-row"
                            onClick={() => setDeleteModal(sub)}
                            title="Delete subscriber"
                            data-testid={`delete-${sub.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="admin-footer-info">
          Showing {filteredSubscribers.length} of {subscribers.length} subscribers
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
